/**
 * Free Gift Logic
 * Adds the selected gift and quantity when the eligible collection reaches the minimum spend.
 * Removes it below that minimum, regardless of other cart items.
 */
(function () {
  let isProcessing = false;

  async function getGiftConfig() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(root + '?sections=free-gift-eligibility');
    if (!response.ok) throw new Error('Unable to load gift eligibility');
    const sections = await response.json();
    const html = sections['free-gift-eligibility'];
    if (!html) throw new Error('Gift eligibility section is missing');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const config = JSON.parse(doc.querySelector('#free-gift-config')?.textContent || '{}');
    if (config.enabled === false) return config;
    if (
      config.enabled !== true ||
      !Array.isArray(config.eligibleProductIds) ||
      !Number.isSafeInteger(config.giftVariantId) || config.giftVariantId <= 0 ||
      typeof config.giftProductHandle !== 'string' || !config.giftProductHandle ||
      !Number.isSafeInteger(config.giftQuantity) || config.giftQuantity < 1 || config.giftQuantity > 10 ||
      !Number.isSafeInteger(config.thresholdCents) || config.thresholdCents <= 0
    ) {
      throw new Error('Gift promotion settings are invalid');
    }
    return {
      ...config,
      eligibleProductIds: new Set(config.eligibleProductIds.map(String)),
    };
  }

  function getCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }).then((r) => r.json());
  }

  function addGift(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity,
            properties: { _gift: 'true' },
          },
        ],
      }),
    }).then((r) => r.json());
  }

  function updateGiftQuantity(itemKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity }),
    }).then((r) => r.json());
  }

  function refreshSections() {
    // Re-render the drawer and cart page sections so the UI updates
    return fetch(
      window.location.pathname + '?sections=grim-drawer,cart-icon-bubble'
    )
      .then((r) => r.json())
      .then((sections) => {
        // Update drawer
        if (sections['grim-drawer']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(
            sections['grim-drawer'],
            'text/html'
          );
          const newDrawer = doc.querySelector('#grim-drawer');
          const drawerEl = document.getElementById('grim-drawer');
          if (newDrawer && drawerEl) {
            const body = drawerEl.querySelector('.drawer-body');
            const footer = drawerEl.querySelector('.drawer-footer');
            const newBody = newDrawer.querySelector('.drawer-body');
            const newFooter = newDrawer.querySelector('.drawer-footer');
            if (body && newBody) body.innerHTML = newBody.innerHTML;
            if (footer && newFooter) footer.innerHTML = newFooter.innerHTML;

            // Update cart class
            const hasItems =
              newDrawer.querySelector('.cart__drawer-items')?.children.length >
              0;
            drawerEl.classList.toggle('cart-has-items', hasItems);
            drawerEl.classList.toggle('cart-empty', !hasItems);
          }
        }
        // Update bubble
        if (sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(
              sections['cart-icon-bubble'],
              'text/html'
            );
            const newBubble = doc.querySelector('.shopify-section');
            if (newBubble) bubble.innerHTML = newBubble.innerHTML;
          }
        }

        // Update cart page if on /cart
        const mainCartItems = document.getElementById('main-cart-items');
        if (mainCartItems) {
          const sectionId = mainCartItems.dataset.id;
          if (sectionId) {
            return fetch(
              window.location.pathname + '?sections=' + sectionId
            )
              .then((r) => r.json())
              .then((cartSections) => {
                if (cartSections[sectionId]) {
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(
                    cartSections[sectionId],
                    'text/html'
                  );
                  const newContents = doc.querySelector('.js-contents');
                  const existing =
                    mainCartItems.querySelector('.js-contents');
                  if (newContents && existing)
                    existing.innerHTML = newContents.innerHTML;
                }
              });
          }
        }
      });
  }

  async function checkAndUpdateGift() {
    if (isProcessing) return;
    isProcessing = true;

    try {
      const [cart, config] = await Promise.all([
        getCart(),
        getGiftConfig(),
      ]);
      if (!config.enabled) return;
      const giftItem = cart.items.find(
        (item) =>
          item.variant_id === config.giftVariantId ||
          item.handle === config.giftProductHandle
      );

      // Only the discount's eligible collection contributes to the threshold.
      const subtotalWithoutGift = cart.items.reduce((sum, item) => {
        if (
          item.variant_id === config.giftVariantId ||
          item.handle === config.giftProductHandle ||
          !config.eligibleProductIds.has(String(item.product_id))
        )
          return sum;
        return sum + item.final_line_price;
      }, 0);

      const qualifies = subtotalWithoutGift >= config.thresholdCents;

      if (qualifies && !giftItem) {
        // Add gift
        await addGift(config.giftVariantId, config.giftQuantity);
        await refreshSections();
      } else if (
        qualifies && giftItem?.properties?._gift === 'true' &&
        giftItem.quantity !== config.giftQuantity
      ) {
        await updateGiftQuantity(giftItem.key, config.giftQuantity);
        await refreshSections();
      } else if (!qualifies && giftItem) {
        // Remove gift
        await updateGiftQuantity(giftItem.key, 0);
        await refreshSections();
      }
    } catch (err) {
      console.error('[Free Gift] Error:', err);
    } finally {
      isProcessing = false;
    }
  }

  function onCartChanged() {
    // Debounce the check slightly to let Shopify's cart settle
    setTimeout(checkAndUpdateGift, 500);
  }

  // Listen for all cart change events from grim-drawer
  document.addEventListener('cartItemUpdated', onCartChanged);
  document.addEventListener('cartItemAdded', onCartChanged);

  // Also listen for the cart:updated event (from add-to-cart buttons)
  document.addEventListener('cart:updated', onCartChanged);

  // Check on page load (handles refresh with existing cart)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () =>
      setTimeout(checkAndUpdateGift, 1000)
    );
  } else {
    setTimeout(checkAndUpdateGift, 1000);
  }
})();
